import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { alert, confirm, haptic } from '../lib/telegram';
import { useT } from '../lib/i18n';
import { TagSheet, type Tag } from '../components/TagSheet';
import { fmtShortDay, fmtTime } from '../lib/time';
import { AddClientSheet, LinkConfirmSheet } from '../components/OfflineSheets';
import { fetchRegulars, runwayChip, type Runway } from '../components/Regulars';

/**
 * Every client as one card on the grey, and the card says what the coach
 * would say out loud about them: how often they come, where their level
 * is, whether the ratings moved this month, and — when something needs
 * doing — what that is, in words.
 *
 * The numbers it replaced (average gap in days, days since) were true
 * and unreadable: nobody thinks "4d rhythm". Somebody comes twice a week
 * or they have gone quiet.
 *
 * Two lines and no filler: a fact the coach has not recorded is left out
 * rather than spelled as "No level set". A card that needs attention says
 * so once, in its badge, and looks like every other card otherwise.
 */

const BASE = import.meta.env.VITE_API_URL ?? '';
let authToken = '';
export const setClientsToken = (t: string) => { authToken = t; };

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', authorization: `Bearer ${authToken}`, ...(init.headers ?? {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `http_${res.status}`);
  }
  return res.status === 204 ? (undefined as T) : res.json();
}

export interface ClientRow {
  id: string; name: string; username: string | null; phone: string | null;
  botStarted: boolean; flagged: boolean; blocked?: boolean; credits: number;
  /** Added by hand, no Telegram (sql/030). */
  offline?: boolean;
  /** Marked «Постоянный» by the coach (sql/037). */
  regular?: boolean;
  totalSessions: number; groups?: Tag[];
  lastSeen: string | null; daysSince: number | null; avgGapDays: number | null;
  status: 'new' | 'regular' | 'slipping' | 'lapsed' | 'never';
  sessions8w: number;
  frequency: 'twiceWeekly' | 'weekly' | 'fortnightly' | 'occasional' | 'none';
  /** The next session they have booked, if they have one. */
  nextSession: string | null;
  level: number | null;
  progress: number | null;
}

/** The two that want doing something about, in the order they want it. */
const ATTENTION: ClientRow['status'][] = ['lapsed', 'slipping'];

function initials(name: string) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('');
}

/**
 * Six soft pairs, tint and its own ink. A name always lands on the same
 * one, so a coach recognises a client by colour before reading the name.
 */
const FACES: [string, string][] = [
  ['#e7eefb', '#2c5ba8'],
  ['#e1f1ed', '#147f72'],
  ['#faede3', '#8f5524'],
  ['#efeaf8', '#6248a6'],
  ['#fbe9ef', '#a33d5e'],
  ['#eaf2e1', '#4b7a2c'],
];

function faceTint(name: string) {
  let n = 0;
  for (const ch of name) n = (n * 31 + (ch.codePointAt(0) ?? 0)) % 100003;
  const [background, color] = FACES[n % FACES.length];
  return { background, color };
}

export function ClientsList({ onOpenClient }: { onOpenClient?: (id: string, name?: string) => void }) {
  const t = useT();
  const [clients, setClients] = useState<ClientRow[] | null>(null);
  const [tags, setTags] = useState<Tag[]>([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<string | null>(null);
  const [tagging, setTagging] = useState<ClientRow | null>(null);
  const [requests, setRequests] = useState<Request[]>([]);
  /**
   * «Без записи»: the regulars whose bookings are running out. Its own
   * fetch, so the list itself stays one query — and all it adds to the
   * screen is a chip on their cards and one chip in the filter row. The
   * warning that needs doing something about reaches the coach in the
   * bot, not by making this list longer.
   */
  const [runway, setRunway] = useState<Runway[]>([]);
  const [onlyRegulars, setOnlyRegulars] = useState(false);
  const [showRequests, setShowRequests] = useState(false);
  const [adding, setAdding] = useState(false);

  const loadRunway = useCallback(() => fetchRegulars()
    .then((r) => setRunway(r.clients)).catch(() => setRunway([])), []);

  const load = () => Promise.all([
    api<{ clients: ClientRow[] }>('/api/coach/insights'),
    api<Tag[]>('/api/coach/groups'),
    api<Request[]>('/api/coach/requests').catch(() => [] as Request[]),
  ])
    .then(([r, g, q]) => { setClients(r.clients); setTags(g); setRequests(q); void loadRunway(); })
    .catch(() => { setClients([]); setTags([]); });

  useEffect(() => { load(); }, []);

  const shown = useMemo(() => (clients ?? []).filter((c) => {
    if (search) {
      const q = search.toLowerCase().replace(/^@/, '');
      const digits = search.replace(/\D/g, '');
      const byPhone = digits.length >= 3 && (c.phone ?? '').replace(/\D/g, '').includes(digits);
      if (!c.name.toLowerCase().includes(q) && !(c.username ?? '').toLowerCase().includes(q) && !byPhone) return false;
    }
    if (filter && !(c.groups ?? []).some((g) => g.id === filter)) return false;
    if (onlyRegulars && !c.regular) return false;
    return true;
  }), [clients, search, filter, onlyRegulars]);

  /**
   * One list, in the order a coach thinks about people: whoever trains most
   * at the top, then whoever trains less, then the ones who have booked but
   * not yet come, then the ones with nothing at all.
   *
   * There is no section for "never booked": a client who booked yesterday
   * belongs among the others, not under a heading saying they never did.
   */
  const ordered = [...shown].sort((a, b) => {
    const rank = (c: ClientRow) =>
      c.totalSessions > 0 ? 0 : c.nextSession ? 1 : 2;
    if (rank(a) !== rank(b)) return rank(a) - rank(b);
    if (rank(a) === 0) {
      if (b.sessions8w !== a.sessions8w) return b.sessions8w - a.sessions8w;
      return (a.daysSince ?? 0) - (b.daysSince ?? 0);
    }
    if (rank(a) === 1) return (a.nextSession ?? '').localeCompare(b.nextSession ?? '');
    return a.name.localeCompare(b.name);
  });
  const regulars = (clients ?? []).filter((c) => c.regular).length;
  const filterTag = tags.find((g) => g.id === filter);

  if (!clients) return <div className="list"><div className="skeleton" /><div className="skeleton" /></div>;

  return (
    <>
      {requests.length > 0 && (
        <NewRequestsRow requests={requests} onOpen={() => { haptic.tap(); setShowRequests(true); }} />
      )}
      {showRequests && (
        <RequestsSheet requests={requests}
          onClose={() => setShowRequests(false)}
          onChanged={() => { load(); window.dispatchEvent(new Event('coach:clients-changed')); }} />
      )}
      <div className="clients__bar">
        <input className="clients__search" type="search" value={search}
          placeholder={t('co.clients.search')}
          onChange={(e) => setSearch(e.target.value)} />
        <button className="off-addbtn" onClick={() => { haptic.tap(); setAdding(true); }}>{t('co.off.add')}</button>

        {(tags.length > 0 || regulars > 0) && (
          <div className="clients__filters">
            <button className={`tagchip${filter === null && !onlyRegulars ? ' tagchip--on' : ''}`}
              onClick={() => { haptic.tap(); setFilter(null); setOnlyRegulars(false); }}>
              {t('co.clients.all')}
            </button>
            {/* Один чип: кто постоянный. У кого кончается запись — видно
                по акцентной дате в строке под именем. */}
            {regulars > 0 && (
              <button className={`tagchip tagchip--reg${onlyRegulars ? ' tagchip--regon' : ''}`}
                onClick={() => { haptic.tap(); setOnlyRegulars(!onlyRegulars); setFilter(null); }}>
                ★ {t('co.reg.filter', { n: regulars })}
              </button>
            )}
            {tags.map((g) => (
              <button key={g.id}
                className={`tagchip${filter === g.id ? ' tagchip--on' : ''}`}
                style={filter === g.id && g.color
                  ? { background: g.color, borderColor: g.color, color: '#fff' }
                  : g.color ? { color: g.color, borderColor: `${g.color}59` } : undefined}
                onClick={() => { haptic.tap(); setFilter(filter === g.id ? null : g.id); setOnlyRegulars(false); }}>
                {/* A tag is the coach's own word: never translated. */}
                {g.name}{g.members ? ` ${g.members}` : ''}
              </button>
            ))}
            {/* Where tags are made and unmade: Setup, not here. */}
            <button className="tagchip tagchip--edit"
              onClick={() => { haptic.tap(); window.dispatchEvent(new Event('coach:edit-tags')); }}>
              {t('co.clients.editTags')}
            </button>
          </div>
        )}
      </div>

      {shown.length === 0 ? (
        <div className="empty">{filter || onlyRegulars ? t('co.clients.noneFiltered') : t('co.clients.none')}</div>
      ) : (
        <div className="clients">
          {ordered.map((c) => (
            <ClientCard key={c.id} c={c} onOpen={onOpenClient} onTag={() => setTagging(c)}
              runway={runway.find((r) => r.clientId === c.id)} />
          ))}

          {(filterTag || onlyRegulars) && (
            <div className="clients__filternote">
              {filterTag ? t('co.clients.onlyShown', { tag: filterTag.name }) : t('co.reg.onlyShown')}{' '}
              <button onClick={() => { haptic.tap(); setFilter(null); setOnlyRegulars(false); }}>
                {t('co.clients.clearFilter')}
              </button>
            </div>
          )}
        </div>
      )}

      {tagging && (
        <TagSheet client={tagging} tags={tags}
          onClose={() => setTagging(null)}
          onChanged={load} />
      )}
      {adding && (
        <AddClientSheet onClose={() => setAdding(false)}
          onAdded={(id) => { setAdding(false); load(); onOpenClient?.(id); }} />
      )}
    </>
  );
}

/** Someone waiting for this coach to approve them. */
interface Request {
  id: string; first_name: string; last_name: string | null;
  username: string | null; requested_at: string;
  /** A client added by hand who looks like this person (sql/030). */
  offlineMatch?: { id: string; name: string; phone: string | null; reason: 'phone' | 'name' } | null;
}
const requestName = (r: Request) => (r.last_name ? `${r.first_name} ${r.last_name}` : r.first_name);

/**
 * New requests, above everyone else: the people waiting for an answer.
 * They are not in the list below until approved — until then they cannot
 * book, so there is nothing else to do with them.
 */
function NewRequestsRow({ requests, onOpen }: { requests: Request[]; onOpen: () => void }) {
  const t = useT();
  const names = requests.slice(0, 2).map(requestName).join(', ') + (requests.length > 2 ? '…' : '');
  return (
    <div className="reqwrap">
      <button className="reqrow" onClick={onOpen} aria-label={t('co.req.aria', { n: requests.length })}>
        <span className="reqrow__faces">
          {requests.slice(0, 3).map((r) => (
            <span key={r.id} className="face reqrow__face" style={faceTint(requestName(r))}>{initials(requestName(r))}</span>
          ))}
        </span>
        <span className="reqrow__text">
          <b>{t('co.req.title')}</b>
          <span>{t(requests.length === 1 ? 'co.req.waitingOne' : 'co.req.waiting', { names })}</span>
        </span>
        <span className="reqrow__count">{requests.length}</span>
      </button>
    </div>
  );
}

function RequestsSheet({ requests, onClose, onChanged }: {
  requests: Request[]; onClose: () => void; onChanged: () => void;
}) {
  const t = useT();
  const [busy, setBusy] = useState<string | null>(null);
  const [linking, setLinking] = useState<Request | null>(null);

  async function answer(r: Request, approve: boolean) {
    if (!approve && !(await confirm(t('co.req.blockConfirm', { name: requestName(r) })))) return;
    setBusy(r.id);
    try {
      if (approve) await api(`/api/coach/clients/${r.id}/approve`, { method: 'POST', body: '{}' });
      else await api(`/api/coach/clients/${r.id}/block`, { method: 'PUT', body: JSON.stringify({ blocked: true }) });
      haptic.success();
      onChanged();
      if (requests.length === 1) onClose();
    } catch (err: any) {
      haptic.error();
      await alert(t('co.failed', { message: err.message }));
    } finally { setBusy(null); }
  }

  return createPortal(<>
    <div className="sheet" onClick={onClose}>
      <div className="sheet__panel" onClick={(e) => e.stopPropagation()}>
        <div className="sheet__title">{t('co.req.title')}</div>
        <div className="card__when">{t('co.req.intro')}</div>
        <div className="reqlist">
          {requests.map((r) => (
            <div key={r.id} className="card reqcard">
              <div className="reqcard__who">
                <span className="face face--lg" style={faceTint(requestName(r))}>{initials(requestName(r))}</span>
                <span>
                  <b>{requestName(r)}</b>
                  <span className="card__when">
                    {r.username ? `@${r.username}` : t('co.req.noUsername')}
                    {' · '}{fmtShortDay(new Date(r.requested_at))}, {fmtTime(r.requested_at)}
                  </span>
                </span>
              </div>
              {r.offlineMatch && (
                <div className="off-match">
                  <span className="off-match__t">{t('co.off.reqMatch')}</span>
                  <span>{r.offlineMatch.name}{r.offlineMatch.reason === 'phone'
                    ? ` · ${t('co.off.samePhone')} ${r.offlineMatch.phone ?? ''}` : ` · ${t('co.off.sameName')}`}</span>
                  <button className="savebtn" disabled={busy !== null}
                    onClick={() => { haptic.tap(); setLinking(r); }}>{t('co.off.linkBtn')}</button>
                </div>
              )}
              <div className="reqcard__actions">
                <button className="reqcard__approve" disabled={busy !== null} onClick={() => answer(r, true)}>
                  {t('co.req.approve')}
                </button>
                <button className="reqcard__block" disabled={busy !== null} onClick={() => answer(r, false)}>
                  {t('co.req.block')}
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="card__when">{t('co.req.foot')}</div>
        <button className="paybar__opt" style={{ width: '100%', marginTop: 10 }} onClick={onClose}>{t('co.req.close')}</button>
      </div>
    </div>
      {linking?.offlineMatch && (
        <LinkConfirmSheet
          offline={{ id: linking.offlineMatch.id, name: linking.offlineMatch.name }}
          into={{ id: linking.id, name: requestName(linking), username: linking.username }}
          onClose={() => setLinking(null)}
          onLinked={async (res) => {
            setLinking(null);
            onChanged();
            await alert(res.upcoming > 0
              ? t('co.off.doneUpcoming', { name: linking.offlineMatch!.name, n: res.upcoming })
              : t('co.off.done', { name: linking.offlineMatch!.name }));
            if (requests.length === 1) onClose();
          }} />
      )}
    </>,
    document.body,
  );
}

function ClientCard({ c, onOpen, onTag, runway }: {
  c: ClientRow; onOpen?: (id: string, name?: string) => void; onTag: () => void;
  /** Set when this client is in «Постоянные без записи вперёд». */
  runway?: Runway;
}) {
  const t = useT();
  const needs = ATTENTION.includes(c.status) && !c.blocked;

  /** Why the badge is there, in the words a coach would use. */
  const why = c.status === 'lapsed' && c.daysSince !== null
    ? t('co.why.lapsed', { n: c.daysSince, days: t.word('co.days', c.daysSince) })
    : c.status === 'slipping' && c.daysSince !== null && c.avgGapDays
      ? t('co.why.slipping', { n: c.daysSince, gap: c.avgGapDays })
      : c.status === 'new' ? t('co.why.new')
      : null;

  /**
   * One line of facts, and only facts there are. Nothing the coach has
   * not recorded gets a sentence of its own saying so.
   */
  const facts: string[] = [];
  // No Telegram: the phone is how the coach reaches them.
  if (c.offline && c.phone) facts.push(c.phone);
  if (c.status === 'never') {
    if (c.nextSession) facts.push(t('co.clients.firstSession', { when: fmtShortDay(new Date(c.nextSession)) }));
    else if (c.username) facts.push(`@${c.username}`, t('co.clients.noSessionsYet'));
    else facts.push(t('co.clients.noSessionsYet'));
  } else {
    // The "why" and the rhythm answer the same question; the why wins.
    if (why) facts.push(why);
    else if (c.frequency !== 'none') facts.push(t(`co.freq.${c.frequency}` as 'co.freq.weekly'));
    if (c.level !== null) facts.push(t('co.level', { n: c.level.toFixed(1) }));
    if (c.progress !== null && Math.abs(c.progress) >= 0.2) {
      const n = `${c.progress > 0 ? '+' : '\u2212'}${Math.abs(c.progress).toFixed(1)}`;
      facts.push(t(c.progress > 0 ? 'co.progress.up' : 'co.progress.down', { n }));
    }
  }

  const tags = c.groups ?? [];

  return (
    <div className="clientcard">
      {/* The card itself opens the client; the chips on top of it do not. */}
      <button className="clientcard__open" aria-label={c.name}
        onClick={() => { haptic.tap(); onOpen?.(c.id, c.name); }} />

      <span className="face face--lg" style={faceTint(c.name)}>{initials(c.name)}</span>

      <span className="clientcard__who">
        <span className="clientcard__line">
          {/* ★ — «постоянный». Звезда clients.flagged убрана: её нечем
              поставить, а знак нужен тому, что коуч действительно ставит. */}
          {c.regular && <i className="clientcard__star" aria-hidden="true">★</i>}
          <span className="clientcard__name">{c.name}</span>
          {c.offline && <span className="off-tag">{t('co.off.tag')}</span>}
          {tags.map((g) => (
            <button key={g.id} className="tagpill"
              style={g.color ? { color: g.color, background: `${g.color}1f` } : undefined}
              onClick={() => { haptic.tap(); onTag(); }}
              aria-label={t('co.tag.addTo', { name: c.name })}>
              {/* A tag is the coach's own word: never translated. */}
              {g.name}
            </button>
          ))}
          {needs && (
            // One red mark, and the facts line says how long it has been.
            <span className="clientcard__alert" role="img"
              aria-label={t(c.status === 'lapsed' ? 'co.status.lapsed' : 'co.status.slipping')}>!</span>
          )}
        </span>
        {/* «Запись до 21 окт.» живёт в той же серой строке, что и ритм,
            просто акцентным цветом: новой строки список не получает. */}
        {(facts.length > 0 || runway) && (
          <span className="clientcard__facts">
            {facts.join(' · ')}
            {runway && facts.length > 0 && ' · '}
            {runway && <em className="runsoon">{runwayChip(t, runway)}</em>}
          </span>
        )}
      </span>
    </div>
  );
}
